import React, { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as yup from 'yup';
import { buildValidationSchema, Confetti } from '@comicrelief/component-library';
import { snakeCase, isEmpty } from 'lodash';
import fatalErrorMessage from './fatalErrorMessage';

import {
  FIELDS,
  FIELDS_DEFAULT_VALUES,
  PRINTED_FUNDRAISING_PACK,
  DEFAULT_FR_GROUPS,
  JUSTGIVING_STORIES,
  DEFAULT_JUSTGIVING_STORY,
  FIELD_MAPPING
} from './constants';
import withRecaptcha from '../../utils/withRecaptcha';
import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import Paragraphs from '../../components/Paragraphs/Paragraphs';
import DataProtectionNotice from './components/DataProtectionNotice';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';

import {
  TopScrollWrapper,
  FsuMainWrapper,
  FormOuterWrapper,
  FormInnerWrapper
} from './FundraiserSignup.style';
import {
  getSchoolId,
  getAddressId,
  getRules,
  // handleCAPI,
  isMobileOrLandline,
  removeSpace,
  phoneValidate
} from './utils';

import transformMarketingPref from '../../components/MarketingPrefs/_utils';
import YourDetailsSection from './YourDetailsSection';
import FundraisingGroupSection from './FundraisingGroupSection';
import JustGivingMarketingSection from './JustGivingMarketingSection';
import SuccessSection from './SuccessSection';
import FundraiserSignupContext from './context/FundraiserSignupContext';
import {
  ContentfulImagePropTypes,
  ContentTextAreaPropTypes
} from './PropTypes';

const trackSubmission = (group, uuid, segment, thisPreselectedGroup, thisCampaignCode) => {
  const event = `cr-${group}-reg`;
  const action = `cr-${group}-registration`;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({
    event,
    action,
    label: 'complete',
    fundraiser_signup_uuid: uuid,
    fsu_url: window.location.pathname,
    fsu_preselected_group: thisPreselectedGroup,
    campaign_code: thisCampaignCode
  });

  // Additional object as requested by Hiral
  window.dataLayer.push({
    event,
    action,
    label: 'complete',
    fundraiser_signup_uuid: uuid,
    fsu_subgroups: `cr-${group}-${segment}-reg`,
    fsu_preselected_group: thisPreselectedGroup,
    fsu_url: window.location.pathname,
    campaign_code: thisCampaignCode
  });
};

const FundraiserSignupPage = ({
  data: {
    contentfulPageFundraiserSignup2022: {
      campaignCode,
      title,
      upperParagraphs,
      lowerParagraphs,
      step1Title,
      step1Introduction,
      showPhoneNumberField,
      showTShirtField,
      step2Title,
      step2Introduction,
      defaultAudienceGroup,
      schoolsImage,
      workImage,
      publicImage,
      step3Title,
      step3Introduction,
      showFundraisingPackQuestion,
      fundraisingPackSegments,
      hideWhyDoWeCollectYourInfoSection,
      successMessage,
      justGivingPageSetup,
      justGivingPageForSchools,
      justGivingPageForWorkplace,
      justGivingPageForMyselfGroup,
      justGivingLabel,
      justGivingDescription,
      formStatus,
      successParagraphs = [],
      showBreadcrumb,
      parentPages,
      backgroundColour = ''
    },
    allContentfulPagePartner,
    allContentfulPagePerson,
    featArticles,
    allContentfulCart,
    allContentfulDescriptor
  },
  pageContext
}) => {
  // Handle any unset fields (which return null) accordingly:
  const showForm = formStatus !== false;

  const { theme } = pageContext || {};
  const { edges } = allContentfulPagePartner || { edges: [] };
  const { edges: people } = allContentfulPagePerson || { edges: [] };
  const { edges: moneyBuys } = allContentfulCart || { edges: [] };
  const articles = featArticles?.edges || [];
  const descData = allContentfulDescriptor || {};
  const setupJG = justGivingPageSetup
    || justGivingPageForSchools
    || justGivingPageForWorkplace
    || justGivingPageForMyselfGroup;

  // Standard FSU: Hide the email input (use the values we've already collected), and
  // disable the postal entry as per https://comicrelief.atlassian.net/browse/ENG-3468
  const validationOverrides = {
    [FIELDS.MP_PERM_EMAIL]: { hideInput: true },
    [FIELDS.MP_PERM_POST]: { disableOption: true }
  };

  const { mpValidationOptions, mpValidationFields } = buildValidationSchema(validationOverrides);

  const addressFields = [FIELDS.LINE1, FIELDS.LINE2, FIELDS.LINE3, FIELDS.TOWN, FIELDS.POSTCODE];

  // Define main form's schema:
  const mainSchema = yup
    .object()
    .shape(getRules(showTShirtField, showPhoneNumberField));

  const combinedSchema = mainSchema.shape(mpValidationFields);

  const methods = useForm({
    resolver: yupResolver(combinedSchema),
    mode: 'onBlur',
    // Letting errorHandler take care of this now to cover all bases:
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: FIELDS_DEFAULT_VALUES
  });

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { isSubmitting },
    formState
  } = methods;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAddressInputs, setShowAddressInputs] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [hasFatalError, setHasFatalError] = useState(false);
  const [userUUID, setUserUUID] = useState(null);
  const [campaignUrl, setCampaignUrl] = useState(null);

  const [hasDefaultGroup, setHasDefaultGroup] = useState(false);
  const [preselectedGroup, setPreselectedGroup] = useState('none');

  const scrollAnchorRef = useRef(null);
  const timeoutRef = useRef(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const step1Options = {
    showPhoneNumberField,
    showTShirtField
  };

  const step2Options = {
    schoolsImage: schoolsImage?.file.url,
    workImage: workImage?.file.url,
    publicImage: publicImage?.file.url
  };

  const fundraisingGroup = watch(FIELDS.GROUP);

  const doScroll = () => {
    if (scrollAnchorRef && scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView();
    }
  };

  // I can't believe react-hook-form doesn't either of these natively!
  const getError = field => formState.errors[field]?.message || '';
  const getErroringFields = fieldArray => fieldArray.filter(field => formState.errors?.[field]);

  // Set up marketing preferences with user's details
  const setupMarketingPreferences = (formValues, name) => {
    // As we update these programmatically below, we
    // need to ignore here to avoid infinite loops:
    if (name && name.startsWith('mp_')) {
      return;
    }

    // If this field is covered in our fieldMapping object...
    if (Object.hasOwn(FIELD_MAPPING, name)) {
      // ...update the associated MP field, assigning an empty
      // string for any null values to keep the MPs happy:
      setValue(FIELD_MAPPING[name], getValues(name) || '', {
        shouldValidate: false
      });
    }

    // Use our helper function to determine which MP field to populate if any.
    // NB: Because we're stripping spaces at a state level (via Yup transform),
    // not from the actual field itself, we need to do the same here:
    let suppliedPhone = getValues(FIELDS.PHONE) || '';

    if (suppliedPhone !== undefined && suppliedPhone !== '') {
      suppliedPhone = removeSpace(suppliedPhone);

      // Only copy phone number to marketing preferences when it's a *valid* phone number;
      // having to use the same pattern here since error state isn't available on field update:
      const isCompletePhoneNumber = phoneValidate.isValidSync(suppliedPhone);

      if (isCompletePhoneNumber && name === FIELDS.PHONE) {
        // Determine which MP options are suitable for this type of number
        const theseMPFields = isMobileOrLandline(suppliedPhone);
        theseMPFields.forEach((field) => {
          setValue(field, suppliedPhone, { shouldValidate: false });
        });
      }
    }
  };

  // Rather than just adding to this directly to render, we can at least restrict it
  // *somewhat* by hooking into formState.error updates specifically:
  useEffect(() => {
    if (!isEmpty(formState.errors)
      && getErroringFields(addressFields).length
      && !showAddressInputs) {
      setShowAddressInputs(true);
    }
  }, [formState.errors]);

  useEffect(() => {
    // I don't know why this fixes things
    watch();

    // On initial mount/render, format any default group setting:
    const formattedGroup = snakeCase(defaultAudienceGroup).toUpperCase();

    // Check it's legit:
    if (Object.keys(DEFAULT_FR_GROUPS).includes(formattedGroup)) {
      // If so, map the CMS value (which is the just dropdown label value),
      // to the value we actually care about:
      setValue(FIELDS.GROUP, DEFAULT_FR_GROUPS[formattedGroup]);
      // Update our flag to let us hide all group buttons:
      setHasDefaultGroup(true);
      setPreselectedGroup(DEFAULT_FR_GROUPS[formattedGroup]);
    }

    // Set up marketing preferences with debouncing to avoid validation conflicts
    watch((formValues, { name }) => {
      // Update our MPs when FSU fields are updating:
      setupMarketingPreferences(formValues, name);
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const submitHandler = async (formData) => {
    try {
      const schoolId = selectedSchool && getSchoolId({ formData, selectedSchool });
      const addressId = selectedAddress && getAddressId({ formData, selectedAddress });
      // const prizeDrawOptIn = Boolean(formData.prize_draw_opt_in);
      const thisFormData = formData;
      let thisUUID;
      // Default to 'no JG' values
      let justGivingStory = null;
      let justGivingPageTitle = null;
      let tools = [];
      // Provivded by the backend to allow us to signpost the
      // user onwards, should there be any submission error:
      let thisCampaignUrl;

      // We no longer offer the user a choice of customisation,
      // this sets the default target amount and fundraising story here,
      // then they can customise within JG itself if they want to.
      if (setupJG) {
        tools = ['justgiving_page_setup'];
        justGivingStory = JUSTGIVING_STORIES[campaignCode] || DEFAULT_JUSTGIVING_STORY;
        justGivingPageTitle = thisFormData.jg_page_title;
      }
      thisFormData.fundraising_target_text = '£250';
      thisFormData.fundraising_target_pounds = 250;

      // Transform the MP permission values now we're using MP's built-in validation
      // NB: A future piece of work will transform within the MP component itself,
      // given the '1/0/null' data requirement seems to be global.
      Object.keys(formData).forEach((field) => {
        if (field.includes('mp_permission')) {
          thisFormData[field] = transformMarketingPref(formData[field]);
        }
      });

      // Massage the fundraising pack back into a usable format by the backend
      if (
        thisFormData[FIELDS.FUNDRAISING_PACK]
        === PRINTED_FUNDRAISING_PACK.YES.value
      ) {
        tools.push('fundraising_pack');
      }
      // remove this field as its now handled by the tools array
      delete thisFormData[FIELDS.FUNDRAISING_PACK];
      // remove the over_eighteen value as this isn't stored.
      delete thisFormData.over_eighteen;

      // As this is optional in the backend, ensure we're not passing a false positive here
      if (!thisFormData[FIELDS.ORG_SIZE]) thisFormData[FIELDS.ORG_SIZE] = null;
      if (!thisFormData[FIELDS.INDUSTRY_TYPE]) thisFormData[FIELDS.INDUSTRY_TYPE] = null;
      if (!thisFormData[FIELDS.JOB]) thisFormData[FIELDS.JOB] = null;

      // Polyfilling for now, while waiting on FSU B/E updates
      if (!thisFormData[FIELDS.MP_LINE2]) thisFormData[FIELDS.MP_LINE2] = '';
      if (!thisFormData[FIELDS.MP_LINE3]) thisFormData[FIELDS.MP_LINE3] = '';
      // Not part of the main form, so having to hardcode this while waiting on FSU B/E updates:
      if (!thisFormData[FIELDS.MP_COUNTRY]) thisFormData[FIELDS.MP_COUNTRY] = 'GB';

      const data = {
        ...thisFormData,
        // Short-term fix as yup's "default" no longer seems to allow
        // us to set an empty string where we need it for the B/E:
        address_line_2: thisFormData.address_line_2 || '',
        address_line_3: thisFormData.address_line_3 || '',
        address_id: addressId,
        org_id: schoolId,
        org_id_type: schoolId && 'crosl',
        campaign_code: campaignCode,
        fundraising_tools: tools,
        // This is no longer being used as part of the form
        // however it's expected by downstream services so we tack it on here.
        fundraising_ideas: [],
        mp_transSourceUrl: window.location.href,
        // prize_draw_opt_in: prizeDrawOptIn
        jg_page_story: justGivingStory,
        jg_page_title: justGivingPageTitle,
        // Manually adding to this to keep B/E happy,
        // now that we've removed it.
        mp_permissionPost: null
      };

      const reCaptchaToken = await executeRecaptcha('fsu');
      await axios
        .post(`${process.env.GATSBY_FSU_SUBMIT_URL}/signup`, data, {
          params: { recaptcha_token: reCaptchaToken },
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        })
        .then((response) => {
          thisUUID = response.data.data.id;
          thisCampaignUrl = response.data.data.jgCampaignUrl;
        });

      trackSubmission(formData[FIELDS.GROUP], thisUUID,
        formData[FIELDS.SEGMENT], preselectedGroup, campaignCode);
      setUserUUID(thisUUID);
      setCampaignUrl(thisCampaignUrl);

      setIsSubmitted(true);
      doScroll();

      // Once we've successfully submitted, pass the userFields to trigger the CAPI event
      // SERVICE STILL BROKEN, COMMENTING OUT FOR NOW:
      // handleCAPI(formData, thisUUID);
    } catch (error) {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(error);
      }
      doScroll();
      setHasFatalError(true);
    }
  };

  const errorHandler = async () => {
    // Give render the time to catch-up:
    setTimeout(() => {
      const firstError = document.querySelector('[data-test="error-message"]');
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
  };

  const hideInfo = Boolean(hideWhyDoWeCollectYourInfoSection);

  // Adding autoComplete="off" to all the text inputs to hopefully improve data quality.
  // (Although I think Chrome ignores this annoyingly...)
  return (
    <Layout backgroundColour={backgroundColour}>
      <FsuMainWrapper
        data-campaign={campaignCode}
        className="fsu-wrapper"
        data-status={`${showForm ? 'open' : 'closed'}`}
      >

        {showBreadcrumb
          && (
            <Breadcrumb
              thisPageTitle={title}
              parentPages={parentPages}
              backgroundColour={backgroundColour}
            />
          )}

        {upperParagraphs && (
          <Paragraphs
            theme={theme}
            paragraphs={upperParagraphs}
            partners={edges}
            people={people}
            articles={articles}
            moneyBuys={moneyBuys}
            descriptors={descData}
          />
        )}

        {showForm && (
          <FormOuterWrapper>
            <FormInnerWrapper>

              <TopScrollWrapper
                ref={scrollAnchorRef}
              />

              <ErrorBoundary
                showError={hasFatalError}
                errorMessage={fatalErrorMessage}
              >
                <FundraiserSignupContext.Provider
                  value={{
                    isSubmitted,
                    register,
                    isSubmitting,
                    getError,
                    watch,
                    fundraisingGroup,
                    methods,
                    setValue,
                    getValues,
                    trigger
                  }}
                >
                  <form
                    onSubmit={handleSubmit(submitHandler, errorHandler)}
                    noValidate
                    autoComplete="off"
                  >

                    <YourDetailsSection
                      title={step1Title}
                      description={step1Introduction}
                      options={step1Options}
                    />

                    <FundraisingGroupSection
                      title={step2Title}
                      description={step2Introduction}
                      showAddressInputs={showAddressInputs}
                      setSelectedAddress={setSelectedAddress}
                      setSelectedSchool={setSelectedSchool}
                      setShowAddressInputs={setShowAddressInputs}
                      options={step2Options}
                      hasDefaultGroup={hasDefaultGroup}
                      campaignCode={campaignCode}
                    />

                    <JustGivingMarketingSection
                      title={step3Title}
                      description={step3Introduction}
                      justGivingLabel={justGivingLabel}
                      justGivingDescription={justGivingDescription}
                      mpValidationOptions={mpValidationOptions}
                      showFundraisingPackQuestion={showFundraisingPackQuestion}
                      fundraisingPackSegments={fundraisingPackSegments}
                      showJGQuestion={justGivingPageSetup}
                      justGivingPageForSchools={justGivingPageForSchools}
                      justGivingPageForWorkplace={justGivingPageForWorkplace}
                      justGivingPageForMyselfGroup={justGivingPageForMyselfGroup}
                      campaignCode={campaignCode}
                    />
                  </form>

                  <SuccessSection
                    successMessage={successMessage}
                    successParagraphs={successParagraphs}
                    userUUID={userUUID}
                    campaignCode={campaignCode}
                    // Only load the loader when the CMS option has the JG integration enabled
                    showJGPageLinkLoader={setupJG}
                    campaignUrl={campaignUrl}
                    theme={theme}
                    partners={edges}
                    people={people}
                    articles={articles}
                    moneyBuys={moneyBuys}
                    descriptors={descData}
                  />

                </FundraiserSignupContext.Provider>

              </ErrorBoundary>

              <Confetti trigger={isSubmitted} />

            </FormInnerWrapper>
          </FormOuterWrapper>
        )}

        {/* The FULL privacy notice copy */}
        {(showForm && !isSubmitted && !hideInfo) && (
          <DataProtectionNotice
            hasPhoneField
            showReducedCopy={hideInfo}
          />
        )}

        {lowerParagraphs && (
          <Paragraphs
            theme={theme}
            paragraphs={lowerParagraphs}
            partners={edges}
            people={people}
            articles={articles}
            moneyBuys={moneyBuys}
            descriptors={descData}
          />
        )}

      </FsuMainWrapper>
    </Layout>
  );
};

FundraiserSignupPage.propTypes = {
  data: PropTypes.shape({
    contentfulPageFundraiserSignup2022: PropTypes.shape({
      backgroundColour: PropTypes.string,
      path: PropTypes.string.isRequired,
      campaignCode: PropTypes.string.isRequired,
      summary: PropTypes.shape({
        summary: PropTypes.string
      }),
      title: PropTypes.string.isRequired,
      upperParagraphs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        __typename: PropTypes.string
      })),
      lowerParagraphs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        __typename: PropTypes.string
      })),
      formStatus: PropTypes.bool,
      imageSeo: ContentfulImagePropTypes,
      step1Title: PropTypes.string.isRequired,
      step1Introduction: ContentTextAreaPropTypes.isRequired,
      showPhoneNumberField: PropTypes.bool.isRequired,
      showTShirtField: PropTypes.bool.isRequired,
      step2Title: PropTypes.string.isRequired,
      step2Introduction: ContentTextAreaPropTypes.isRequired,
      defaultAudienceGroup: PropTypes.string,
      schoolsImage: ContentfulImagePropTypes,
      workImage: ContentfulImagePropTypes,
      publicImage: ContentfulImagePropTypes,
      step3Title: PropTypes.string.isRequired,
      step3Introduction: ContentTextAreaPropTypes.isRequired,
      showFundraisingPackQuestion: PropTypes.bool.isRequired,
      fundraisingPackSegments: PropTypes.arrayOf(PropTypes.string),
      hideWhyDoWeCollectYourInfoSection: PropTypes.bool.isRequired,
      justGivingPageSetup: PropTypes.bool.isRequired,
      justGivingPageForSchools: PropTypes.bool.isRequired,
      justGivingPageForWorkplace: PropTypes.bool.isRequired,
      justGivingPageForMyselfGroup: PropTypes.bool.isRequired,
      justGivingLabel: PropTypes.string.isRequired,
      justGivingDescription: ContentTextAreaPropTypes.isRequired,
      successMessage: ContentTextAreaPropTypes.isRequired,
      // eslint-disable-next-line
      successParagraphs: PropTypes.arrayOf(PropTypes.object),
      showBreadcrumb: PropTypes.bool,
      parentPages: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          path: PropTypes.string.isRequired
        })
      )
    }).isRequired,
    allContentfulPagePartner: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      }))
    }),
    allContentfulPagePerson: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      }))
    }),
    featArticles: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      }))
    }),
    allContentfulCart: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      }))
    }),
    allContentfulDescriptor: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      }))
    })
  }).isRequired,
  pageContext: PropTypes.shape({
    theme: PropTypes.string
  })
};

export const Head = props => (
  <SEO
    allData={props}
    pageType={SEO_PAGE_TYPES.FSU}
  />
);

export const pageQuery = graphql`
  query FundraiserSignupPage($id: String) {
    contentfulPageFundraiserSignup2022(id: { eq: $id }) {
      path
      campaignCode
      title
      backgroundColour
      formStatus
      showBreadcrumb
      parentPages {
        ... on Node {
          ... on ContentfulPageLandingPage {
            title
            path
          }
          ... on ContentfulPageDefault {
            title
            path
          }
        }
      }
      upperParagraphs {
        ...SingleMessageParagraph
        ...CtaMultiCard
        ...CtaSingleCard
        ...CardsParagraph
        ...HeroBanner
        ...QuoteSlice
        ...DonateBanner
        ...StatsSlice
        ...EmailBanner
      }
      lowerParagraphs {
        ...SingleMessageParagraph
        ...CtaMultiCard
        ...CtaSingleCard
        ...CardsParagraph
        ...HeroBanner
        ...QuoteSlice
        ...DonateBanner
        ...StatsSlice
        ...EmailBanner
      }
      summary {
        summary
      }
      imageSeo {
        ...Picture
      }
      step1Title

      step1Introduction {
        raw
      }
      showTShirtField
      showPhoneNumberField
      step2Title
      step2Introduction {
        raw
      }
      defaultAudienceGroup
      schoolsImage {
        file {
          url
        }
      }
      workImage {
        file {
          url
        }
      }
      publicImage {
        file {
          url
        }
      }
      step3Title
      step3Introduction {
        raw
      }
      showFundraisingPackQuestion
      fundraisingPackSegments
      hideWhyDoWeCollectYourInfoSection
      justGivingPageSetup
      justGivingPageForSchools,
      justGivingPageForWorkplace,
      justGivingPageForMyselfGroup,
      justGivingLabel
      justGivingDescription {
        raw
      }
      successMessage {
        raw
      }
      successParagraphs {
        ...HeroBanner
        ...QuoteSlice
        ...DonateBanner
        ...StatsSlice
        ...EmailBanner
      }
    }
    allContentfulPagePartner(sort: {weight: ASC}) {
      edges {
        node {
          ...PartnerList
        }
      }
    }
    allContentfulPagePerson(filter: {type: {ne: null}}) {
      edges {
        node {
          ...PeopleList
        }
      }
    }
    featArticles: allContentfulPageArticle(
      filter: {type: {eq: "News Article"}, excludeFromFeatureArticles: {ne: "Yes"}}
      limit: 3
      sort: {displayDate: DESC}
    ) {
      edges {
        node {
          title
          categories {
            title
          }
          path
          type
          id
          displayDate(formatString: "D MMMM YYYY")
          image {
            ...Picture
          }
        }
      }
    }
    allContentfulCart {
      edges {
        node {
          id
          cartId
          contentful_id
          singleGiving {
            moneybuys {
              description
              value
            }
          }
          regularGiving {
            moneybuys {
              description
              value
            }
          }
        }
      }
    }
    allContentfulDescriptor(sort: {date: DESC}) {
      group(field: {category: SELECT}) {
        fieldValue
        totalCount
      }
      edges {
        node {
          ...Descriptor
        }
      }
    }
  }
`;

export default withRecaptcha(FundraiserSignupPage);
